import { type ServiceIdentifier } from '@blocksuite/global/di';
import { DisposableGroup } from '@blocksuite/global/disposable';
import {
  Bound,
  clamp,
  getBoundWithRotation,
  Point,
} from '@blocksuite/global/gfx';
import {
  canvasResizeLimitOf,
  constrainCanvasWorldDelta,
} from '@blocksuite/store';
import { signal } from '@preact/signals-core';
import last from 'lodash-es/last.js';

import type { PointerEventState } from '../../event/state/pointer.js';
import { getTopElements } from '../../utils/tree.js';
import type { GfxBlockComponent } from '../../view/index.js';
import { GfxExtension, GfxExtensionIdentifier } from '../extension.js';
import { GfxBlockElementModel } from '../model/gfx-block-model.js';
import type { GfxModel } from '../model/model.js';
import { GfxPrimitiveElementModel } from '../model/surface/element-model.js';
import {
  createCanvasDragPreviewState,
  hasRemoteCanvasDragPreviewLock,
} from '../remote-drag-preview.js';
import type { GfxElementModelView } from '../view/view.js';
import { createInteractionContext, type SupportedEvents } from './event.js';
import {
  type ActionContextMap,
  type InteractivityActionAPI,
  type InteractivityEventAPI,
  InteractivityExtensionIdentifier,
} from './extension/base.js';
import {
  type GfxViewInteractionConfig,
  GfxViewInteractionIdentifier,
} from './extension/view.js';
import { GfxViewEventManager } from './gfx-view-event-handler.js';
import {
  DEFAULT_HANDLES,
  type OptionResize,
  ResizeController,
  type RotateOption,
} from './resize/manager.js';
import {
  isHandleRatioLocked,
  proportionalMemberBound,
  resolveSelectionResizeHandles,
} from './resize/member-bound.js';
import type { RequestElementsCloneContext } from './types/clone.js';
import type {
  DragExtensionInitializeContext,
  DragInitializationOption,
  ExtensionDragEndContext,
  ExtensionDragMoveContext,
  ExtensionDragStartContext,
} from './types/drag.js';
import type {
  BoxSelectionContext,
  ResizeConstraint,
  RotateConstraint,
  SelectContext,
} from './types/view.js';

type ExtensionPointerHandler = Exclude<
  SupportedEvents,
  'pointerleave' | 'pointerenter'
>;

export const InteractivityIdentifier = GfxExtensionIdentifier(
  'interactivity-manager'
) as ServiceIdentifier<InteractivityManager>;

export class InteractivityManager extends GfxExtension {
  static override key = 'interactivity-manager';

  private _activeCanvasDragPreviewId: string | null = null;
  private _activeCanvasDragPreviewMoveElements:
    | { model: GfxModel; bound: Bound }[]
    | null = null;

  private readonly _disposable = new DisposableGroup();

  private canvasEventHandler = new GfxViewEventManager(this.gfx);

  override mounted(): void {
    this.canvasEventHandler = new GfxViewEventManager(this.gfx);
    this.interactExtensions.forEach(ext => {
      ext.mounted();
    });
  }

  activeInteraction$ = signal<null | {
    type: 'move' | 'resize' | 'rotate';
    elements: GfxModel[];
  } | null>(null);

  override unmounted(): void {
    if (this._activeCanvasDragPreviewId) {
      this._clearLocalCanvasDragPreview(this._activeCanvasDragPreviewId);
      this._activeCanvasDragPreviewId = null;
    }
    this._disposable.dispose();
    this.interactExtensions.forEach(ext => {
      ext.unmounted();
    });
  }

  get interactExtensions() {
    return this.std.provider.getAll(InteractivityExtensionIdentifier);
  }

  get keyboard() {
    return this.gfx.keyboard;
  }

  private _safeExecute(fn: () => void, errorMessage: string) {
    try {
      fn();
    } catch (e) {
      console.error(errorMessage, e);
    }
  }

  private _clearLocalCanvasDragPreview(dragId: string) {
    const awarenessStore = this.std.store.awarenessStore;
    const current = awarenessStore.getLocalState()?.canvasDragPreview;
    if (current?.dragId !== dragId) {
      return;
    }
    awarenessStore.setLocalStateField('canvasDragPreview', undefined);
    this._activeCanvasDragPreviewMoveElements = null;
    this._activeCanvasDragPreviewDimensions?.clear();
  }

  private _activeCanvasDragPreviewDimensions = new Map<
    string,
    { w: number; h: number; rotate: number }
  >();

  private _createCanvasDragPreviewId(event: PointerEvent | MouseEvent) {
    const pointerId = 'pointerId' in event ? event.pointerId : 0;
    return `${this.std.store.awarenessStore.awareness.clientID}:${pointerId}:${Date.now()}`;
  }

  private _getCanvasDragPreviewBound(
    model: GfxModel,
    bound: Bound,
    operation: 'move' | 'resize' | 'rotate'
  ) {
    if (
      operation !== 'move' ||
      !(model instanceof GfxBlockElementModel) ||
      !model.transientXYWH
    ) {
      return bound;
    }

    // Auto-sized blocks can own a live height that differs from persisted xywh.
    // Publish their visible size for movement, while keeping the drag position
    // and persistent final-bound contract unchanged. Resize uses its own bounds.
    const visible = Bound.deserialize(model.transientXYWH);
    return new Bound(bound.x, bound.y, visible.w, visible.h);
  }

  private _publishLocalCanvasDragPreview(input: {
    dragId: string;
    elements: {
      bound: Bound;
      model: GfxModel;
    }[];
    operation?: 'move' | 'resize' | 'rotate';
    startCursor: Point;
  }) {
    this._activeCanvasDragPreviewId = input.dragId;
    this._activeCanvasDragPreviewMoveElements =
      (input.operation ?? 'move') === 'move' ? input.elements : null;
    this._activeCanvasDragPreviewDimensions = new Map();
    this.std.store.awarenessStore.setLocalStateField(
      'canvasDragPreview',
      createCanvasDragPreviewState({
        dragId: input.dragId,
        elements: input.elements.map(({ bound, model }) => {
          const visible = this._getCanvasDragPreviewBound(
            model,
            bound,
            input.operation ?? 'move'
          );
          this._activeCanvasDragPreviewDimensions.set(model.id, {
            w: visible.w,
            h: visible.h,
            rotate: model.rotate,
          });
          return {
            elementId: model.id,
            rotate: model.rotate,
            xywh: visible.serialize(),
          };
        }),
        operation: input.operation,
        startCursor: {
          x: input.startCursor.x,
          y: input.startCursor.y,
        },
        updatedAt: Date.now(),
        translation:
          (input.operation ?? 'move') === 'move' ? { x: 0, y: 0 } : undefined,
        cursor: input.startCursor,
        basisVersion: (input.operation ?? 'move') === 'move' ? 0 : undefined,
      })
    );
  }

  private _translateLocalCanvasDragPreview(
    dragId: string,
    translation: { x: number; y: number },
    cursor?: { x: number; y: number }
  ) {
    const awarenessStore = this.std.store.awarenessStore;
    const current = awarenessStore.getLocalState()?.canvasDragPreview;
    if (
      current?.dragId !== dragId ||
      current.status !== 'dragging' ||
      current.operation !== 'move'
    )
      return;
    const elements = this._activeCanvasDragPreviewMoveElements;
    if (!elements) return;
    let basisChanged = false;
    const frames = elements.map(({ bound, model }) => {
      const visible = this._getCanvasDragPreviewBound(
        model,
        bound.moveDelta(translation.x, translation.y),
        'move'
      );
      const previous = this._activeCanvasDragPreviewDimensions.get(model.id);
      if (
        !previous ||
        previous.w !== visible.w ||
        previous.h !== visible.h ||
        previous.rotate !== model.rotate
      ) {
        basisChanged = true;
        this._activeCanvasDragPreviewDimensions.set(model.id, {
          w: visible.w,
          h: visible.h,
          rotate: model.rotate,
        });
      }
      return {
        elementId: model.id,
        rotate: model.rotate,
        xywh: visible.serialize(),
      };
    });
    // Keep the existing absolute-frame contract for already open clients.
    // Reuse the drag-start positions, but read live block sizes on each update.
    awarenessStore.setLocalStateField('canvasDragPreview', {
      ...current,
      elements: frames,
      translation,
      cursor: cursor ?? {
        x: current.startCursor.x + translation.x,
        y: current.startCursor.y + translation.y,
      },
      basisVersion: (current.basisVersion ?? 0) + (basisChanged ? 1 : 0),
      updatedAt: Date.now(),
    });
  }

  private _refreshLocalCanvasDragPreview(dragId: string, now = Date.now()) {
    const awarenessStore = this.std.store.awarenessStore;
    const current = awarenessStore.getLocalState()?.canvasDragPreview;
    if (current?.dragId !== dragId || current.status !== 'dragging') {
      return;
    }
    awarenessStore.setLocalStateField('canvasDragPreview', {
      ...current,
      updatedAt: now,
    });
  }

  private _settleLocalCanvasDragPreview(input: {
    dragId: string;
    elements: {
      finalBound: Bound;
      model: GfxModel;
    }[];
    operation?: 'move' | 'resize' | 'rotate';
    startCursor: Point;
  }) {
    const awarenessStore = this.std.store.awarenessStore;
    const current = awarenessStore.getLocalState()?.canvasDragPreview;
    if (current?.dragId !== input.dragId) {
      return;
    }
    this._activeCanvasDragPreviewMoveElements = null;
    awarenessStore.setLocalStateField('canvasDragPreview', {
      dragId: input.dragId,
      elements: input.elements.map(({ finalBound, model }) => ({
        elementId: model.id,
        rotate: model.rotate,
        xywh: this._getCanvasDragPreviewBound(
          model,
          finalBound,
          input.operation ?? current.operation
        ).serialize(),
      })),
      elementIds: input.elements.map(({ model }) => model.id),
      operation: input.operation ?? current.operation,
      startCursor: {
        x: input.startCursor.x,
        y: input.startCursor.y,
      },
      status: 'settling' as const,
      updatedAt: Date.now(),
    });
    setTimeout(() => this._clearLocalCanvasDragPreview(input.dragId), 1_000);
  }

  private _getCanvasDragPreviewElements(models: GfxModel[]) {
    return models.map(model => ({
      bound: Bound.deserialize(model.xywh),
      model,
    }));
  }

  /**
   * Dispatch event to extensions and gfx view.
   * @param eventName
   * @param evt
   * @returns
   */
  dispatchEvent(eventName: ExtensionPointerHandler, evt: PointerEventState) {
    if (this.activeInteraction$.peek()) {
      return;
    }

    const { context, preventDefaultState } = createInteractionContext(evt);
    const extensions = this.interactExtensions;

    extensions.forEach(ext => {
      (ext.event as InteractivityEventAPI).emit(eventName, context);
    });

    const handledByView = this._clickedBlockCovers(eventName, evt)
      ? false
      : (this.canvasEventHandler.dispatch(eventName, evt) ?? false);

    return {
      preventDefaultState,
      handledByView,
    };
  }

  /**
   * Whether the event is a click on a block that lies over the drawn element
   * under the pointer. The click is the block's then, like the selection it
   * makes, and the element under the block takes none of it.
   */
  private _clickedBlockCovers(
    eventName: ExtensionPointerHandler,
    evt: PointerEventState
  ) {
    if (eventName !== 'click' && eventName !== 'dblclick') return false;
    const under = this.canvasEventHandler.top?.model;
    if (!(under instanceof GfxPrimitiveElementModel)) return false;
    const { target } = this._pressedAt(evt.raw);
    return (
      target instanceof GfxBlockElementModel &&
      this.gfx.layer.compare(under, target) < 0
    );
  }

  private _getSelectionConfig(models: GfxModel[]) {
    type SelectionHandlers = Required<
      ReturnType<Required<GfxViewInteractionConfig>['handleSelection']>
    >;

    const selectionConfigMap = new Map<
      string,
      {
        view: GfxBlockComponent | GfxElementModelView;
        handlers: SelectionHandlers;
        defaultHandlers: SelectionHandlers;
      }
    >();

    models.forEach(model => {
      const typeOrFlavour = 'flavour' in model ? model.flavour : model.type;
      const view = this.gfx.view.get(model);
      const config = this.std.getOptional(
        GfxViewInteractionIdentifier(typeOrFlavour)
      );

      if (!view) {
        return;
      }

      const selectionConfig =
        config?.handleSelection?.({
          gfx: this.gfx,
          std: this.std,
          view,
          model,
        }) ?? {};
      const defaultHandlers = {
        selectable: () => {
          return !model.isLockedByAncestor();
        },
        onSelect: (context: SelectContext) => {
          if (context.multiSelect) {
            this.gfx.selection.toggle(model);
          } else {
            this.gfx.selection.set({ elements: [model.id] });
          }

          return true;
        },
      };

      selectionConfigMap.set(model.id, {
        view,
        defaultHandlers,
        handlers: {
          ...defaultHandlers,
          ...selectionConfig,
        },
      });
    });

    return selectionConfigMap;
  }

  private _getSuggestedTarget(context: {
    candidates: GfxModel[];
    target: GfxModel;
  }) {
    const { candidates, target } = context;

    const suggestedElements: {
      id: string;
      priority?: number;
    }[] = [];
    const suggest = (element: { id: string; priority?: number }) => {
      suggestedElements.push(element);
    };

    const extensions = this.interactExtensions;
    extensions
      .values()
      .toArray()
      .forEach(ext => {
        return (ext.action as InteractivityActionAPI).emit('elementSelect', {
          candidates,
          target,
          suggest,
        });
      });

    if (suggestedElements.length) {
      suggestedElements.sort((a, b) => {
        return (a.priority ?? 0) - (b.priority ?? 0);
      });

      const suggested = last(suggestedElements) as {
        id: string;
        priority?: number;
      };
      const elm = this.gfx.getElementById(suggested.id);

      return elm instanceof GfxPrimitiveElementModel ||
        elm instanceof GfxBlockElementModel
        ? elm
        : target;
    }

    return target;
  }

  /**
   * What a press at the event's point is on: the top-most element lying there
   * that takes a selection, or the one the extensions suggest in its place.
   */
  private _pressedAt(raw: PointerEvent, at: [number, number] = [raw.x, raw.y]) {
    const { gfx } = this;
    const [x, y] = gfx.viewport.toModelCoordFromClientCoord(at);
    let candidates = this.gfx.getElementByPoint(x, y, {
      all: true,
    });

    const selectionConfigs = this._getSelectionConfig(candidates);
    const context = {
      multiSelect: raw.shiftKey,
      event: raw,
      position: Point.from([x, y]),
    };

    candidates = candidates.filter(model => {
      if (!selectionConfigs.has(model.id)) {
        return false;
      }
      const config = selectionConfigs.get(model.id)!;

      return (
        selectionConfigs.has(model.id) &&
        selectionConfigs.get(model.id)?.handlers.selectable({
          ...context,
          view: config.view,
          model,
          default: config.defaultHandlers.selectable as () => boolean,
        })
      );
    });

    const top = last(candidates);

    return {
      target: top && this._getSuggestedTarget({ candidates, target: top }),
      selectionConfigs,
      position: context.position,
    };
  }

  /**
   * The element a plain press at a point of the window lands on, as a
   * selection takes it; null on a free spot.
   */
  elementPressedAt(x: number, y: number): GfxModel | null {
    const press = new PointerEvent('pointerdown', { clientX: x, clientY: y });
    return this._pressedAt(press, [x, y]).target ?? null;
  }

  /**
   * Handle element selection.
   * @param evt The pointer event that triggered the selection.
   * @returns True if the element was selected, false otherwise.
   */
  handleElementSelection(evt: PointerEventState) {
    const { raw } = evt;
    const { gfx } = this;
    const { target, selectionConfigs, position } = this._pressedAt(raw);

    {
      if (!target) {
        return false;
      }

      const config = selectionConfigs.has(target.id)
        ? selectionConfigs.get(target.id)
        : this._getSelectionConfig([target]).get(target.id);

      if (!config) {
        return false;
      }

      const multiSelect = raw.shiftKey;
      const context = {
        selected: multiSelect ? !gfx.selection.has(target.id) : true,
        multiSelect,
        event: raw,
        position,
      };

      const result = config.handlers.onSelect({
        ...context,
        selected: multiSelect ? !gfx.selection.has(target.id) : true,
        view: config.view,
        model: target,
        default: config.defaultHandlers.onSelect as () => void,
      });

      return result ?? true;
    }
  }

  handleBoxSelection(context: { box: BoxSelectionContext['box'] }) {
    const elements = this.gfx.getElementsByBound(context.box).filter(model => {
      const view = this.gfx.view.get(model);

      if (
        !view ||
        view.onBoxSelected({
          box: context.box,
        }) === false
      )
        return false;

      return true;
    });

    return getTopElements(elements).filter(elm => !elm.isLocked());
  }

  /**
   * Initialize elements movements.
   * It will handle drag start, move and end events automatically.
   * Note: Call this when mouse is already down.
   */
  handleElementMove(options: DragInitializationOption) {
    let cancelledByExt = false;

    const context: DragExtensionInitializeContext = {
      /**
       * The elements that are being dragged
       */
      elements: options.movingElements,

      preventDefault: () => {
        cancelledByExt = true;
      },

      dragStartPos: Point.from(
        this.gfx.viewport.toModelCoordFromClientCoord([
          options.event.x,
          options.event.y,
        ])
      ),
    };
    const extension = this.interactExtensions;
    const activeExtensionHandlers = Array.from(
      extension.values().map(ext => {
        return (ext.action as InteractivityActionAPI).emit(
          'dragInitialize',
          context
        );
      })
    );

    if (cancelledByExt) {
      activeExtensionHandlers.forEach(handler => handler?.clear?.());
      return;
    }

    if (
      hasRemoteCanvasDragPreviewLock({
        elementIds: context.elements.map(element => element.id),
        localClientId: this.std.store.awarenessStore.awareness.clientID,
        now: Date.now(),
        states: this.std.store.awarenessStore.getStates(),
      })
    ) {
      activeExtensionHandlers.forEach(handler => handler?.clear?.());
      return;
    }

    // Extensions can add descendants already expanded by the drag initiator.
    // Run every view and publish every presence member exactly once.
    context.elements = Array.from(
      new Map(context.elements.map(model => [model.id, model])).values()
    );

    const host = this.std.host;
    const doc = host.ownerDocument;
    const win = doc.defaultView;
    const { event } = options;
    let activeCanvasDragPreviewId: string | null = null;
    let lastCanvasDragPreviewHeartbeat = 0;
    let dragFinished = false;
    const internal = {
      elements: context.elements.map(model => {
        return {
          view: this.gfx.view.get(model)!,
          originalBound: Bound.deserialize(model.xywh),
          worldBound: getBoundWithRotation(model),
          model: model,
        };
      }),
      dragStartPos: Point.from(
        this.gfx.viewport.toModelCoordFromClientCoord([event.x, event.y])
      ),
    };
    let dragLastPos = internal.dragStartPos;
    let lastEvent = event;

    const viewportWatcher = this.gfx.viewport.viewportMoved.subscribe(() => {
      onDragMove(lastEvent as PointerEvent);
    });
    const onDragMove = (event: PointerEvent) => {
      lastEvent = event;
      dragLastPos = Point.from(
        this.gfx.viewport.toModelCoordFromClientCoord([event.x, event.y])
      );

      const moveContext: ExtensionDragMoveContext = {
        ...internal,
        event,
        dragLastPos,
        dx: dragLastPos.x - internal.dragStartPos.x,
        dy: dragLastPos.y - internal.dragStartPos.y,
      };

      // If shift key is pressed, restrict the movement to one direction
      if (this.keyboard.shiftKey$.peek()) {
        const angle = Math.abs(Math.atan2(moveContext.dy, moveContext.dx));
        const direction =
          angle < Math.PI / 4 || angle > 3 * (Math.PI / 4) ? 'dy' : 'dx';

        moveContext[direction] = 0;
      }

      this._safeExecute(() => {
        activeExtensionHandlers.forEach(handler =>
          handler?.onDragMove?.(moveContext)
        );
      }, 'Error while executing extension `onDragMove`');

      Object.assign(
        moveContext,
        constrainCanvasWorldDelta(
          internal.elements.map(element => element.worldBound),
          moveContext.dx,
          moveContext.dy
        )
      );

      internal.elements.forEach(element => {
        const { view, originalBound } = element;

        view.onDragMove({
          currentBound: originalBound,
          dx: moveContext.dx,
          dy: moveContext.dy,
          elements: internal.elements,
        });
      });

      if (activeCanvasDragPreviewId) {
        const now = Date.now();
        this._translateLocalCanvasDragPreview(
          activeCanvasDragPreviewId,
          {
            x: moveContext.dx,
            y: moveContext.dy,
          },
          dragLastPos
        );
        if (now - lastCanvasDragPreviewHeartbeat > 1_000) {
          this._refreshLocalCanvasDragPreview(activeCanvasDragPreviewId, now);
          lastCanvasDragPreviewHeartbeat = now;
        }
      }
    };
    const onDragEndFromLastEvent = () => {
      onDragEnd(lastEvent as PointerEvent);
    };
    const onVisibilityChange = () => {
      if (doc.visibilityState === 'hidden') {
        onDragEndFromLastEvent();
      }
    };
    const onDragEnd = (event: PointerEvent) => {
      if (dragFinished) {
        return;
      }
      dragFinished = true;
      this.activeInteraction$.value = null;

      host.removeEventListener('pointermove', onDragMove, false);
      host.removeEventListener('pointerup', onDragEnd, false);
      host.removeEventListener('pointercancel', onDragEnd, false);
      host.removeEventListener(
        'lostpointercapture',
        onDragEndFromLastEvent,
        false
      );
      win?.removeEventListener('pointerup', onDragEnd, false);
      win?.removeEventListener('pointercancel', onDragEnd, false);
      win?.removeEventListener('blur', onDragEndFromLastEvent, false);
      doc.removeEventListener('visibilitychange', onVisibilityChange, false);
      viewportWatcher.unsubscribe();

      dragLastPos = Point.from(
        this.gfx.viewport.toModelCoordFromClientCoord([event.x, event.y])
      );
      const endContext: ExtensionDragEndContext = {
        ...internal,
        event,
        dragLastPos,
        dx: dragLastPos.x - internal.dragStartPos.x,
        dy: dragLastPos.y - internal.dragStartPos.y,
      };

      if (this.keyboard.shiftKey$.peek()) {
        const angle = Math.abs(Math.atan2(endContext.dy, endContext.dx));
        endContext[
          angle < Math.PI / 4 || angle > (3 * Math.PI) / 4 ? 'dy' : 'dx'
        ] = 0;
      }

      this.std.store.transact(() => {
        // Layout extensions may pop stashed fields directly into Y.Map.
        // Include their writes and cleanup in the same tracked drop as views.
        this._safeExecute(() => {
          activeExtensionHandlers.forEach(handler =>
            handler?.onDragEnd?.(endContext)
          );
        }, 'Error while executing extension `onDragEnd` handler');

        Object.assign(
          endContext,
          constrainCanvasWorldDelta(
            internal.elements.map(element => element.worldBound),
            endContext.dx,
            endContext.dy
          )
        );

        internal.elements.forEach(element => {
          const { view, originalBound } = element;

          view.onDragEnd({
            currentBound: originalBound.moveDelta(endContext.dx, endContext.dy),
            dx: endContext.dx,
            dy: endContext.dy,
            elements: internal.elements,
          });
        });

        this._safeExecute(() => {
          activeExtensionHandlers.forEach(handler => handler?.clear?.());
        }, 'Error while executing extension `clear` handler');
      });

      if (activeCanvasDragPreviewId) {
        this._settleLocalCanvasDragPreview({
          dragId: activeCanvasDragPreviewId,
          elements: internal.elements.map(({ model, originalBound }) => ({
            finalBound: originalBound.moveDelta(endContext.dx, endContext.dy),
            model,
          })),
          startCursor: dragLastPos,
        });
        activeCanvasDragPreviewId = null;
        this._activeCanvasDragPreviewId = null;
      }

      options.onDragEnd?.();
    };
    const listenEvent = () => {
      host.addEventListener('pointermove', onDragMove, false);
      host.addEventListener('pointerup', onDragEnd, false);
      host.addEventListener('pointercancel', onDragEnd, false);
      host.addEventListener(
        'lostpointercapture',
        onDragEndFromLastEvent,
        false
      );
      win?.addEventListener('pointerup', onDragEnd, false);
      win?.addEventListener('pointercancel', onDragEnd, false);
      win?.addEventListener('blur', onDragEndFromLastEvent, false);
      doc.addEventListener('visibilitychange', onVisibilityChange, false);
    };
    const dragStart = () => {
      this.activeInteraction$.value = {
        type: 'move',
        elements: context.elements,
      };

      internal.elements.forEach(({ view, originalBound }) => {
        view.onDragStart({
          currentBound: originalBound,
          elements: internal.elements,
        });
      });

      activeCanvasDragPreviewId = this._createCanvasDragPreviewId(event);
      lastCanvasDragPreviewHeartbeat = Date.now();
      this._publishLocalCanvasDragPreview({
        dragId: activeCanvasDragPreviewId,
        elements: internal.elements.map(({ model, originalBound }) => ({
          bound: originalBound,
          model,
        })),
        startCursor: internal.dragStartPos,
      });

      const dragStartContext: ExtensionDragStartContext = {
        ...internal,
        event: event as PointerEvent,
        dragLastPos,
      };

      this._safeExecute(() => {
        activeExtensionHandlers.forEach(handler =>
          handler?.onDragStart?.(dragStartContext)
        );
      }, 'Error while executing extension `onDragStart` handler');
    };

    listenEvent();
    dragStart();
  }

  handleElementRotate(
    options: Omit<
      RotateOption,
      'onRotateStart' | 'onRotateEnd' | 'onRotateUpdate'
    > & {
      onRotateUpdate?: (payload: {
        currentAngle: number;
        delta: number;
      }) => void;
      onRotateStart?: () => void;
      onRotateEnd?: () => void;
    }
  ) {
    const { rotatable, viewConfigMap, initialRotate } =
      this._getViewRotateConfig(options.elements);

    if (!rotatable) {
      return;
    }

    const handler = new ResizeController({ gfx: this.gfx });
    const elements = Array.from(viewConfigMap.values()).map(
      config => config.view.model
    ) as GfxModel[];
    if (
      hasRemoteCanvasDragPreviewLock({
        elementIds: elements.map(element => element.id),
        localClientId: this.std.store.awarenessStore.awareness.clientID,
        now: Date.now(),
        states: this.std.store.awarenessStore.getStates(),
      })
    ) {
      return;
    }
    let activeCanvasDragPreviewId: string | null = null;
    const rotateStartCursor = Point.from(
      this.gfx.viewport.toModelCoordFromClientCoord([
        options.event.clientX,
        options.event.clientY,
      ])
    );

    handler.startRotate({
      ...options,
      elements,
      onRotateStart: payload => {
        this.activeInteraction$.value = {
          type: 'rotate',
          elements,
        };
        options.onRotateStart?.();
        payload.data.forEach(({ model }) => {
          if (!viewConfigMap.has(model.id)) {
            return;
          }

          const { handlers, defaultHandlers, view, constraint } =
            viewConfigMap.get(model.id)!;

          handlers.onRotateStart({
            default: defaultHandlers.onRotateStart as () => void,
            constraint,
            model,
            view,
          });
        });
        activeCanvasDragPreviewId = this._createCanvasDragPreviewId(
          options.event
        );
        this._publishLocalCanvasDragPreview({
          dragId: activeCanvasDragPreviewId,
          elements: this._getCanvasDragPreviewElements(elements),
          operation: 'rotate',
          startCursor: rotateStartCursor,
        });
      },
      onRotateUpdate: payload => {
        options.onRotateUpdate?.({
          currentAngle: initialRotate + payload.delta,
          delta: payload.delta,
        });
        payload.data.forEach(
          ({
            model,
            newBound,
            originalBound,
            newRotate,
            originalRotate,
            matrix,
          }) => {
            if (!viewConfigMap.has(model.id)) {
              return;
            }

            const { handlers, defaultHandlers, view, constraint } =
              viewConfigMap.get(model.id)!;

            handlers.onRotateMove({
              model,
              newBound,
              originalBound,
              newRotate,
              originalRotate,
              default: defaultHandlers.onRotateMove as () => void,
              constraint,
              view,
              matrix,
            });
          }
        );
        if (activeCanvasDragPreviewId) {
          this._publishLocalCanvasDragPreview({
            dragId: activeCanvasDragPreviewId,
            elements: this._getCanvasDragPreviewElements(elements),
            operation: 'rotate',
            startCursor: rotateStartCursor,
          });
        }
      },
      onRotateEnd: payload => {
        this.activeInteraction$.value = null;
        options.onRotateEnd?.();
        this.std.store.transact(() => {
          payload.data.forEach(({ model }) => {
            if (!viewConfigMap.has(model.id)) {
              return;
            }

            const { handlers, defaultHandlers, view, constraint } =
              viewConfigMap.get(model.id)!;

            handlers.onRotateEnd({
              default: defaultHandlers.onRotateEnd as () => void,
              view,
              model,
              constraint,
            });
          });
        });
        if (activeCanvasDragPreviewId) {
          this._settleLocalCanvasDragPreview({
            dragId: activeCanvasDragPreviewId,
            elements: this._getCanvasDragPreviewElements(elements).map(
              ({ bound, model }) => ({
                finalBound: bound,
                model,
              })
            ),
            operation: 'rotate',
            startCursor: rotateStartCursor,
          });
          activeCanvasDragPreviewId = null;
          this._activeCanvasDragPreviewId = null;
        }
      },
    });
  }

  private _getViewRotateConfig(elements: GfxModel[]) {
    const deleted = new Set<GfxModel>();
    const added = new Set<GfxModel>();
    const del = (model: GfxModel) => {
      deleted.add(model);
    };
    const add = (model: GfxModel) => {
      added.add(model);
    };

    type ViewRotateHandlers = Required<
      ReturnType<Required<GfxViewInteractionConfig>['handleRotate']>
    >;

    const viewConfigMap = new Map<
      string,
      {
        model: GfxModel;
        view: GfxElementModelView | GfxBlockComponent;
        handlers: ViewRotateHandlers;
        defaultHandlers: ViewRotateHandlers;
        constraint: Required<RotateConstraint>;
      }
    >();

    const addToConfigMap = (model: GfxModel) => {
      const flavourOrType = 'type' in model ? model.type : model.flavour;
      const interactionConfig = this.std.getOptional(
        GfxViewInteractionIdentifier(flavourOrType)
      );
      const view = this.gfx.view.get(model);

      if (!view) {
        return;
      }

      const defaultHandlers: ViewRotateHandlers = {
        beforeRotate: () => {},
        onRotateStart: context => {
          if (!context.constraint.rotatable) {
            return;
          }

          if (model instanceof GfxBlockElementModel) {
            if (Object.hasOwn(model.props, 'rotate')) {
              // @ts-expect-error prop existence has been checked
              model.stash('rotate');
              model.stash('xywh');
            }
          } else {
            model.stash('rotate');
            model.stash('xywh');
          }
        },
        onRotateEnd: context => {
          if (!context.constraint.rotatable) {
            return;
          }

          if (model instanceof GfxBlockElementModel) {
            if (Object.hasOwn(model.props, 'rotate')) {
              // @ts-expect-error prop existence has been checked
              model.pop('rotate');
              model.pop('xywh');
            }
          } else {
            model.pop('rotate');
            model.pop('xywh');
          }
        },
        onRotateMove: context => {
          if (!context.constraint.rotatable) {
            return;
          }

          const { newBound, newRotate } = context;
          model.rotate = newRotate;
          model.xywh = newBound.serialize();
        },
      };
      const handlers = interactionConfig?.handleRotate?.({
        std: this.std,
        gfx: this.gfx,
        view,
        model,
        delete: del,
        add,
      });

      viewConfigMap.set(model.id, {
        model,
        view,
        defaultHandlers,
        handlers: Object.assign({}, defaultHandlers, handlers ?? {}),
        constraint: {
          rotatable: true,
        },
      });
    };

    elements.forEach(addToConfigMap);

    deleted.forEach(model => {
      if (viewConfigMap.has(model.id)) {
        viewConfigMap.delete(model.id);
      }
    });

    added.forEach(model => {
      if (viewConfigMap.has(model.id)) {
        return;
      }

      addToConfigMap(model);
    });

    const views = Array.from(viewConfigMap.values().map(item => item.view));

    let rotatable = true;
    viewConfigMap.forEach(config => {
      const handlers = config.handlers;

      handlers.beforeRotate({
        set: (newConstraint: RotateConstraint) => {
          Object.assign(config.constraint, newConstraint);
          rotatable = rotatable && config.constraint.rotatable;
        },
        elements: views,
      });
    });

    return {
      initialRotate: views.length > 1 ? 0 : (views[0]?.model.rotate ?? 0),
      rotatable,
      viewConfigMap,
    };
  }

  private _getViewResizeConfig(elements: GfxModel[]) {
    const deleted = new Set<GfxModel>();
    const added = new Set<GfxModel>();
    const del = (model: GfxModel) => {
      deleted.add(model);
    };
    const add = (model: GfxModel) => {
      added.add(model);
    };

    type ViewResizeHandlers = Required<
      ReturnType<Required<GfxViewInteractionConfig>['handleResize']>
    >;

    const viewConfigMap = new Map<
      string,
      {
        model: GfxModel;
        view: GfxElementModelView | GfxBlockComponent;
        constraint: Required<ResizeConstraint>;
        handlers: ViewResizeHandlers;
        defaultHandlers: ViewResizeHandlers;
      }
    >();
    const addToConfigMap = (model: GfxModel) => {
      const flavourOrType = 'type' in model ? model.type : model.flavour;
      const interactionConfig = this.std.getOptional(
        GfxViewInteractionIdentifier(flavourOrType)
      );
      const view = this.gfx.view.get(model);

      if (!view) {
        return;
      }

      const defaultHandlers: ViewResizeHandlers = {
        beforeResize: () => {},
        onResizeStart: () => {
          model.stash('xywh');
        },
        onResizeEnd: () => {
          model.pop('xywh');
        },
        onResizeMove: context => {
          const { newBound, constraint } = context;
          const { minWidth, minHeight, maxWidth, maxHeight } = constraint;

          newBound.w = clamp(newBound.w, minWidth, maxWidth);
          newBound.h = clamp(newBound.h, minHeight, maxHeight);

          model.xywh = newBound.serialize();
        },
      };
      const handlers = interactionConfig?.handleResize?.({
        std: this.std,
        gfx: this.gfx,
        view,
        model,
        delete: del,
        add,
      });

      viewConfigMap.set(model.id, {
        model,
        view,
        constraint: {
          lockRatio: false,
          allowedHandlers: DEFAULT_HANDLES,
          // The sizes a kind allows are the canvas' shared table, not a view's own numbers.
          ...canvasResizeLimitOf(flavourOrType),
          ...interactionConfig?.resizeConstraint,
        },
        defaultHandlers,
        handlers: Object.assign({}, defaultHandlers, handlers ?? {}),
      });
    };

    elements.forEach(addToConfigMap);

    deleted.forEach(model => {
      if (viewConfigMap.has(model.id)) {
        viewConfigMap.delete(model.id);
      }
    });

    added.forEach(model => {
      if (viewConfigMap.has(model.id)) {
        return;
      }

      addToConfigMap(model);
    });

    const views = Array.from(viewConfigMap.values().map(item => item.view));

    viewConfigMap.forEach(config => {
      const currConstraint: Required<ResizeConstraint> = config.constraint;

      config.handlers.beforeResize({
        set: (newConstraint: ResizeConstraint) => {
          Object.assign(currConstraint, newConstraint);
        },
        elements: views,
      });

      config.constraint = currConstraint;
    });

    return {
      allowedHandlers: resolveSelectionResizeHandles(
        Array.from(
          viewConfigMap
            .values()
            .map(config => config.constraint.allowedHandlers)
        ),
        DEFAULT_HANDLES
      ),
      viewConfigMap,
    };
  }

  getRotateConfig(options: { elements: GfxModel[] }) {
    return this._getViewRotateConfig(options.elements);
  }

  getSelectionBorderRadius(options: { elements: GfxModel[] }): number {
    if (options.elements.length !== 1) return 0;

    const model = options.elements[0];
    const flavourOrType = 'type' in model ? model.type : model.flavour;
    const config = this.std.getOptional(
      GfxViewInteractionIdentifier(flavourOrType)
    );
    const view = this.gfx.view.get(model);
    const configuredRadius = config?.selectionBorderRadius;

    if (!view || configuredRadius === undefined) return 0;

    const radius =
      typeof configuredRadius === 'function'
        ? configuredRadius({
            std: this.std,
            gfx: this.gfx,
            view,
            model,
          })
        : configuredRadius;

    return Number.isFinite(radius) ? Math.max(0, radius) : 0;
  }

  getResizeHandlers(options: { elements: GfxModel[] }) {
    return this._getViewResizeConfig(options.elements).allowedHandlers;
  }

  handleElementResize(
    options: Omit<
      OptionResize,
      | 'lockRatio'
      | 'onResizeStart'
      | 'onResizeEnd'
      | 'onResizeUpdate'
      | 'onResizeMove'
    > & {
      onResizeStart?: () => void;
      onResizeEnd?: () => void;
      onResizeUpdate?: (payload: {
        lockRatio: boolean;
        scaleX: number;
        scaleY: number;
        exceed: {
          w: boolean;
          h: boolean;
        };
      }) => void;
    }
  ) {
    const { viewConfigMap, allowedHandlers } = this._getViewResizeConfig(
      options.elements
    );

    if (!allowedHandlers.includes(options.handle)) {
      return;
    }

    const { handle } = options;
    const controller = new ResizeController({ gfx: this.gfx });
    const elements = Array.from(viewConfigMap.values()).map(
      config => config.view.model
    ) as GfxModel[];
    if (
      hasRemoteCanvasDragPreviewLock({
        elementIds: elements.map(element => element.id),
        localClientId: this.std.store.awarenessStore.awareness.clientID,
        now: Date.now(),
        states: this.std.store.awarenessStore.getStates(),
      })
    ) {
      return;
    }
    const extensionHandlers = this.interactExtensions.values().reduce(
      (handlers, ext) => {
        const extHandlers = (ext.action as InteractivityActionAPI).emit(
          'elementResize',
          {
            elements,
          }
        );

        if (extHandlers) {
          handlers.push(extHandlers);
        }

        return handlers;
      },
      [] as ActionContextMap['elementResize']['returnType'][]
    );
    let lockRatio = false;
    let activeCanvasDragPreviewId: string | null = null;
    const resizeStartCursor = Point.from(
      this.gfx.viewport.toModelCoordFromClientCoord([
        options.event.clientX,
        options.event.clientY,
      ])
    );
    const latestResizeBounds = new Map<string, Bound>();

    // A member's own ratio lock governs the selection only when it is the
    // selection; inside a multi-selection it applies to that member's share.
    if (viewConfigMap.size === 1) {
      viewConfigMap.forEach(config => {
        lockRatio = isHandleRatioLocked(config.constraint.lockRatio, handle);
      });
    }

    controller.startResize({
      ...options,
      lockRatio,
      elements,
      onResizeMove: ({
        scaleX,
        scaleY,
        originalBound,
        handleSign,
        handlePos,
        currentHandlePos,
        lockRatio,
      }) => {
        const suggested: {
          scaleX: number;
          scaleY: number;
          priority?: number;
        }[] = [];
        const suggest = (distance: { scaleX: number; scaleY: number }) => {
          suggested.push(distance);
        };

        extensionHandlers.forEach(ext => {
          ext.onResizeMove?.({
            scaleX,
            scaleY,
            elements,
            handle,
            handleSign,
            handlePos,
            originalBound,
            currentHandlePos,
            lockRatio,
            suggest,
          });
        });

        suggested.sort((a, b) => {
          return (a.priority ?? 0) - (b.priority ?? 0);
        });

        return last(suggested) ?? { scaleX, scaleY };
      },
      onResizeStart: ({ handleSign, handlePos, data }) => {
        this.activeInteraction$.value = {
          type: 'resize',
          elements,
        };
        extensionHandlers.forEach(ext => {
          ext.onResizeStart?.({
            elements,
            handle,
            handlePos,
            handleSign,
          });
        });

        options.onResizeStart?.();
        data.forEach(({ model }) => {
          if (!viewConfigMap.has(model.id)) {
            return;
          }

          const { handlers, defaultHandlers, view, constraint } =
            viewConfigMap.get(model.id)!;

          handlers.onResizeStart({
            handle,
            default: defaultHandlers.onResizeStart as () => void,
            constraint,
            model,
            view,
          });
        });

        activeCanvasDragPreviewId = this._createCanvasDragPreviewId(
          options.event
        );
        this._publishLocalCanvasDragPreview({
          dragId: activeCanvasDragPreviewId,
          elements: elements.map(model => ({
            bound: Bound.deserialize(model.xywh),
            model,
          })),
          operation: 'resize',
          startCursor: resizeStartCursor,
        });
      },
      onResizeUpdate: ({ data, scaleX, scaleY, lockRatio }) => {
        const exceed = {
          w: false,
          h: false,
        };

        data.forEach(
          ({ model, newBound, originalBound, lockRatio, matrix }) => {
            if (!viewConfigMap.has(model.id)) {
              return;
            }

            const { handlers, defaultHandlers, view, constraint } =
              viewConfigMap.get(model.id)!;
            const memberLocked =
              !lockRatio && isHandleRatioLocked(constraint.lockRatio, handle);

            handlers.onResizeMove({
              model,
              newBound: memberLocked
                ? proportionalMemberBound(originalBound, newBound, handle)
                : newBound,
              originalBound,
              handle,
              default: defaultHandlers.onResizeMove as () => void,
              constraint,
              view,
              lockRatio: lockRatio || memberLocked,
              matrix,
            });
            latestResizeBounds.set(model.id, Bound.deserialize(model.xywh));

            exceed.w =
              exceed.w ||
              model.w === constraint.minWidth ||
              model.w === constraint.maxWidth;
            exceed.h =
              exceed.h ||
              model.h === constraint.minHeight ||
              model.h === constraint.maxHeight;
          }
        );

        options.onResizeUpdate?.({ scaleX, scaleY, lockRatio, exceed });
        if (activeCanvasDragPreviewId) {
          this._publishLocalCanvasDragPreview({
            dragId: activeCanvasDragPreviewId,
            elements: data.map(({ model }) => ({
              bound:
                latestResizeBounds.get(model.id) ??
                Bound.deserialize(model.xywh),
              model,
            })),
            operation: 'resize',
            startCursor: resizeStartCursor,
          });
        }
      },
      onResizeEnd: ({ handleSign, handlePos, data }) => {
        this.activeInteraction$.value = null;

        extensionHandlers.forEach(ext => {
          ext.onResizeEnd?.({
            elements,
            handle,
            handlePos,
            handleSign,
          });
        });
        options.onResizeEnd?.();
        this.std.store.transact(() => {
          data.forEach(({ model }) => {
            if (!viewConfigMap.has(model.id)) {
              return;
            }

            const { handlers, defaultHandlers, view, constraint } =
              viewConfigMap.get(model.id)!;

            handlers.onResizeEnd({
              default: defaultHandlers.onResizeEnd as () => void,
              view,
              model,
              constraint,
              handle,
            });
          });
        });
        if (activeCanvasDragPreviewId) {
          this._settleLocalCanvasDragPreview({
            dragId: activeCanvasDragPreviewId,
            elements: data.map(({ model }) => ({
              finalBound:
                latestResizeBounds.get(model.id) ??
                Bound.deserialize(model.xywh),
              model,
            })),
            operation: 'resize',
            startCursor: resizeStartCursor,
          });
          activeCanvasDragPreviewId = null;
          this._activeCanvasDragPreviewId = null;
        }
      },
    });
  }

  requestElementClone(options: RequestElementsCloneContext) {
    const extensions = this.interactExtensions;

    for (let ext of extensions.values()) {
      const cloneData = (ext.action as InteractivityActionAPI).emit(
        'elementsClone',
        options
      );

      if (cloneData) {
        return cloneData;
      }
    }

    return Promise.resolve(undefined);
  }
}
